import { useState, useEffect } from 'react';
import jsonata from 'jsonata';
import {
  InteractionStepType,
  IndexedInteractionStepResponse,
  ChatJsonataContext,
  StepParamsForTypeWithStage,
} from '@scale/shared/types/chat';
import logger from 'react-scale/lib/logging';

function fillTemplate(template: string, params: { [key: string]: string | undefined }): string {
  const result = template.replace(/\$\{\s*([^}]+)\s*\}/g, (_, key) => {
    const trimmedKey = key?.trim();
    return params[trimmedKey] ?? `\${${trimmedKey}}`;
  });
  return result;
}

export const evaluatePromptText = async (
  initialPromptText: string | undefined,
  responses: IndexedInteractionStepResponse[],
  stepParams: StepParamsForTypeWithStage<InteractionStepType>,
): Promise<string | undefined> => {
  if (initialPromptText) {
    const jsonataContext: ChatJsonataContext = { metadata: { responses }, stepParams };
    const possibleJsonataExpressions: string[] = [];
    const regex = /\$\{\s*([^}]+)\s*\}/g;
    let match;

    while ((match = regex.exec(initialPromptText)) !== null) {
      possibleJsonataExpressions.push(match[1].trim());
    }
    const variables: Record<string, string> = {};
    for (const possibleJsonataExpression of possibleJsonataExpressions) {
      try {
        const result = await jsonata(possibleJsonataExpression).evaluate(jsonataContext);
        variables[possibleJsonataExpression] = result;
      } catch {
        // Skip
      }
    }
    return fillTemplate(initialPromptText, variables);
  }
  return initialPromptText;
};

export const useEvaluatePromptText = (
  initialPromptText: string | undefined,
  responses: IndexedInteractionStepResponse[],
  stepParams: StepParamsForTypeWithStage<InteractionStepType>,
): { evaluatedPromptText: string | undefined; isEvaluatingPrompt: boolean } => {
  const [evaluatedPromptText, setEvaluatedPromptText] = useState<string | undefined>(
    initialPromptText,
  );
  const [isEvaluatingPrompt, setIsEvaluatingPrompt] = useState(true);

  useEffect(() => {
    const _evaluatePromptText = async () => {
      try {
        setEvaluatedPromptText(await evaluatePromptText(initialPromptText, responses, stepParams));
      } catch (error) {
        logger.warn(
          `[useEvaluatePromptText] Error evaluating jsonata: ${error.message}. This run will not include jsonata evaluations.`,
        );
      } finally {
        setIsEvaluatingPrompt(false);
      }
    };
    _evaluatePromptText();
  }, [initialPromptText, responses, stepParams]);
  return { evaluatedPromptText, isEvaluatingPrompt };
};

export default useEvaluatePromptText;
